import React from 'react';
import { dateRangeType } from '../../components/PageDatePicker/types';
import { getURLParams, replaceURLParams } from '../../utils/util';
import dayjs, { Dayjs } from 'dayjs';
import { DateUtils } from '../../utils/date';

const useDateRangeState = (callback?: () => void, initialRange?: { from: Dayjs; to: Dayjs }) => {
  const currentDate = new Date();
  const initialDateRange = {
    startDate:
      initialRange?.from.toDate() || new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    endDate: initialRange?.to.toDate() || currentDate,
    key: 'selection',
  };
  const params = getURLParams();

  const [dateRange, setDateRange] = React.useState<dateRangeType[]>([
    {
      ...initialDateRange,
      startDate: params['from'] ? new Date(+params['from'] * 1000) : initialDateRange.startDate,
      endDate: params['to'] ? new Date(+params['to'] * 1000) : initialDateRange.endDate,
    },
  ]);

  const applyRange = (updater: React.SetStateAction<dateRangeType[]>) => {
    setDateRange((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      replaceURLParams({
        from: dayjs(next[0].startDate).unix(),
        to: dayjs(next[0].endDate).unix(),
      });
      // Call callback function if it exists
      callback && callback();
      return next;
    });
  };

  // Month-range helpers: always snap to full-month boundaries so the selected range
  // can never fall mid-month (used by the ledger statement screens). Both use functional
  // updaters so back-to-back calls (static ranges set from + to synchronously) compose
  // instead of the second call clobbering the first with stale state.
  const setFromMonth = (newValue: Dayjs) => {
    applyRange((prev) => {
      const from = newValue.startOf('month');
      const currentEnd = dayjs(prev[0].endDate);
      const end = from.isAfter(currentEnd) ? from.endOf('month') : currentEnd;
      return [{ startDate: from.toDate(), endDate: end.toDate(), key: 'selection' }];
    });
  };
  const setToMonth = (newValue: Dayjs) => {
    applyRange((prev) => [
      {
        startDate: prev[0].startDate,
        endDate: newValue.endOf('month').toDate(),
        key: 'selection',
      },
    ]);
  };

  return {
    get: dateRange,
    set: applyRange,
    title: DateUtils.getTimeRangeTitle(dateRange[0].startDate, dateRange[0].endDate),
    monthTitle: `${dayjs(dateRange[0].startDate).format('MM/YY')} - ${dayjs(
      dateRange[0].endDate,
    ).format('MM/YY')}`,
    setFromMonth,
    setToMonth,
  };
};

export default useDateRangeState;
